import React, { useMemo } from "react";
import CustomSelect from "components/Form/CustomSelect";
import { useLang } from "hooks/useLang";
import nationalities from "seed/nationality/nationality.json";
import countries from "seed/country/country.json";
import cities from "seed/city/city.json";

interface Option {
  label: string;
  value: string;
}

interface DataItem {
  label: string;
  value: string;
  label_ar: string;
}

interface ReusableSelectProps {
  type: "country" | "nationality" | "city";
  label: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  [x: string]: any;
}

const ReusableSelect: React.FC<ReusableSelectProps> = ({
  type,
  label,
  name,
  value,
  onChange,
  ...props
}) => {
  const { isAr } = useLang();

  const data = useMemo<DataItem[]>(() => {
    switch (type) {
      case "nationality":
        return nationalities as DataItem[];
      case "country":
        return countries as DataItem[];
      case "city":
        return cities as DataItem[];
      default:
        return [];
    }
  }, [type]);

  const options = useMemo<Option[]>(() => {
    const mappedData = data.map((item) => ({
      label: isAr ? item.label_ar || item.label : item.label,
      value: item.label,
    }));

    return mappedData.sort((a, b) =>
      a.label.localeCompare(b.label, isAr ? "ar" : "en")
    );
  }, [data, isAr]);

  const selectedOption = useMemo<Option | null>(() => {
    if (!value) return null;
    const selectedItem = data.find(
      (item) => item.label === value || item.value === value
    );
    if (!selectedItem) {
      return { label: value, value: value };
    }
    return {
      label: isAr
        ? selectedItem.label_ar || selectedItem.label
        : selectedItem.label,
      value: selectedItem.label,
    };
  }, [value, data, isAr]);

  return (
    <CustomSelect
      label={label}
      name={name}
      options={options}
      value={selectedOption}
      onChange={(e: { value: string }) => onChange(e.value)}
      {...props}
    />
  );
};

export default ReusableSelect;
